export const MESSAGE_ATTACHMENT_MAX_COUNT = 10;
export const MESSAGE_ATTACHMENT_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MESSAGE_ATTACHMENT_MAX_BATCH_BYTES = 50 * 1024 * 1024;

const allowedTypesByExtension: Readonly<Record<string, readonly string[]>> = {
  '.7z': ['application/x-7z-compressed'],
  '.bin': ['application/octet-stream'],
  '.csv': ['text/csv', 'text/plain'],
  '.doc': ['application/msword'],
  '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  '.gif': ['image/gif'],
  '.gz': ['application/gzip', 'application/x-gzip'],
  '.jpeg': ['image/jpeg'],
  '.jpg': ['image/jpeg'],
  '.json': ['application/json', 'text/json'],
  '.log': ['text/plain'],
  '.md': ['text/markdown', 'text/plain'],
  '.odp': ['application/vnd.oasis.opendocument.presentation'],
  '.ods': ['application/vnd.oasis.opendocument.spreadsheet'],
  '.odt': ['application/vnd.oasis.opendocument.text'],
  '.pdf': ['application/pdf'],
  '.png': ['image/png'],
  '.ppt': ['application/vnd.ms-powerpoint'],
  '.pptx': ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
  '.rar': ['application/vnd.rar', 'application/x-rar-compressed'],
  '.tar': ['application/x-tar'],
  '.txt': ['text/plain'],
  '.webp': ['image/webp'],
  '.xls': ['application/vnd.ms-excel'],
  '.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  '.yaml': ['application/yaml', 'text/yaml', 'application/x-yaml', 'text/x-yaml', 'text/plain'],
  '.yml': ['application/yaml', 'text/yaml', 'application/x-yaml', 'text/x-yaml', 'text/plain'],
  '.zip': ['application/zip', 'application/x-zip-compressed'],
};

export const MESSAGE_ATTACHMENT_ACCEPT = [
  ...Object.keys(allowedTypesByExtension),
  ...new Set(Object.values(allowedTypesByExtension).flat()),
].join(',');

export type AttachmentSelectionError =
  | 'unsafe-name'
  | 'unsupported-type'
  | 'mime-mismatch'
  | 'file-too-large'
  | 'too-many-files'
  | 'batch-too-large';

export type AttachmentSelectionResult =
  { ok: true; files: File[] } | { ok: false; error: AttachmentSelectionError; filename?: string };

export function appendMessageAttachments(
  existing: readonly File[],
  selected: Iterable<File>,
): AttachmentSelectionResult {
  const additions = Array.from(selected);
  if (existing.length + additions.length > MESSAGE_ATTACHMENT_MAX_COUNT) {
    return { ok: false, error: 'too-many-files' };
  }
  for (const file of additions) {
    const error = validateMessageAttachment(file);
    if (error) {
      return { ok: false, error, filename: file.name };
    }
  }
  const files = [...existing, ...additions];
  const totalBytes = files.reduce((total, file) => total + file.size, 0);
  if (totalBytes > MESSAGE_ATTACHMENT_MAX_BATCH_BYTES) {
    return { ok: false, error: 'batch-too-large' };
  }
  return { ok: true, files };
}

export function validateMessageAttachment(
  file: Pick<File, 'name' | 'size' | 'type'>,
): AttachmentSelectionError | null {
  if (
    !file.name ||
    file.name === '.' ||
    file.name === '..' ||
    file.name.length > 255 ||
    /[/\\\u0000-\u001f\u007f]/u.test(file.name)
  ) {
    return 'unsafe-name';
  }
  if (file.size > MESSAGE_ATTACHMENT_MAX_FILE_BYTES) {
    return 'file-too-large';
  }
  const extensionIndex = file.name.lastIndexOf('.');
  const extension = extensionIndex >= 0 ? file.name.slice(extensionIndex).toLowerCase() : '';
  const allowedTypes = allowedTypesByExtension[extension];
  if (!allowedTypes) {
    return 'unsupported-type';
  }
  const contentType = file.type.split(';', 1)[0]?.trim().toLowerCase() ?? '';
  if (
    contentType &&
    contentType !== 'application/octet-stream' &&
    !allowedTypes.includes(contentType)
  ) {
    return 'mime-mismatch';
  }
  return null;
}

export function isPreviewableImageFile(file: Pick<File, 'name' | 'type'>): boolean {
  const extensionIndex = file.name.lastIndexOf('.');
  const extension = extensionIndex >= 0 ? file.name.slice(extensionIndex).toLowerCase() : '';
  const contentType = file.type.split(';', 1)[0]?.trim().toLowerCase() ?? '';
  return (
    ['.gif', '.jpeg', '.jpg', '.png', '.webp'].includes(extension) &&
    allowedTypesByExtension[extension]?.includes(contentType) === true
  );
}

export function isPreviewableAttachmentContentType(contentType: string): boolean {
  return ['image/gif', 'image/jpeg', 'image/png', 'image/webp'].includes(
    contentType.split(';', 1)[0]?.trim().toLowerCase() ?? '',
  );
}
